import React from 'react';

import AemComponent from '@/components/Aem/AemComponent/AemComponent';
import EditableText from '@/components/Aem/EditableText/EditableText';
import {AppButton} from '@/components/Button';
import {HelpBlueIcon, LocationBlueIcon} from '@/components/Icons';
import appConfig from '@/config/appConfig.typed';
import {
    ADVANCED_DIAGNOSTICS_SUPPORT_CONTACT_US_BUTTON_ARIA_LABEL,
    ADVANCED_DIAGNOSTICS_SUPPORT_FIND_STORE_BUTTON_ARIA_LABEL,
} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import {getAriaLabel} from '@/services/accessibilityService';
import {twx} from '@/utils/tailwindUtils';

const SupportAndStoreTemplate = ({className, isTitleVisible}) => {
    const isMobile = useIsMobileLayout();
    const supportExternalLink = appConfig.getSupportExternalLink();
    const {redirectToExternalLink: redirectToSupport} = useConfigExternalLink(supportExternalLink);

    const storeExternalLink = appConfig.getIqosStoreExternalLink();
    const {redirectToExternalLink: redirectToStore} = useConfigExternalLink(storeExternalLink);

    const isSupportTitleVisible = supportExternalLink || storeExternalLink;
    const isOtherWaysToSupportVisible = isSupportTitleVisible && isTitleVisible;
    const sectionHeading = isOtherWaysToSupportVisible ? 'h3' : 'h2';

    return (
        <div className='my-0 w-full'>
            {isOtherWaysToSupportVisible && (
                <h2 className='mb-10 mt-10 text-24 md:mt-16'>
                    <AemComponent
                        component={EditableText}
                        pageName={routePaths.ADVANCE_DIAGNOSTIC}
                        itemName='supportothertitle'
                    />
                </h2>
            )}
            <div
                className={twx(
                    'flex w-full flex-col items-center md:w-auto ml:flex-row ml:gap-8',
                    {'mt-[60px]': !isSupportTitleVisible || !isTitleVisible},
                    className
                )}
            >
                {supportExternalLink && (
                    <Section
                        {...(isMobile ? {primary: true} : {secondary: true})}
                        icon={<HelpBlueIcon />}
                        heading={sectionHeading}
                        titleKey='supportcontactustitle'
                        onClick={redirectToSupport}
                        buttonTextKey='supportcontactusbutton'
                        buttonAriaLabelKey={ADVANCED_DIAGNOSTICS_SUPPORT_CONTACT_US_BUTTON_ARIA_LABEL}
                    />
                )}
                {storeExternalLink && (
                    <Section
                        secondary
                        icon={<LocationBlueIcon />}
                        heading={sectionHeading}
                        titleKey='supportfindstoretitle'
                        onClick={redirectToStore}
                        buttonTextKey='supportfindstorebutton'
                        buttonAriaLabelKey={ADVANCED_DIAGNOSTICS_SUPPORT_FIND_STORE_BUTTON_ARIA_LABEL}
                    />
                )}
            </div>
        </div>
    );
};

const Section = (props) => {
    const {icon, heading: Heading, titleKey, buttonTextKey, buttonAriaLabelKey, onClick, ...rest} = props;

    return (
        <div className='w-full max-w-full md:self-stretch ml:flex ml:flex-col ml:items-center ml:text-center'>
            {icon && <div className='hidden ml:block'>{icon}</div>}
            {titleKey && (
                <Heading className='mt-3 mb-0 hidden flex-1 text-18 md:mt-4 ml:block'>
                    <AemComponent
                        component={EditableText}
                        pageName={routePaths.ADVANCE_DIAGNOSTIC}
                        itemName={titleKey}
                    />
                </Heading>
            )}
            <AppButton
                light
                onClick={onClick}
                className='mt-4 w-full ml:min-w-0'
                aria-label={getAriaLabel(buttonAriaLabelKey)}
                {...rest}
            >
                <AemComponent
                    component={EditableText}
                    pageName={routePaths.ADVANCE_DIAGNOSTIC}
                    itemName={buttonTextKey}
                />
            </AppButton>
        </div>
    );
};

export default SupportAndStoreTemplate;
